import React, { useState, useEffect } from 'react';
import { Twitter } from 'lucide-react';
import { TwitterQRCode, TwitterQRCodeType } from '../../types/qr-types';
import { t } from '../../i18n/translations';

interface TwitterFormProps {
  data: TwitterQRCode;
  onChange: (data: TwitterQRCode) => void;
  language: 'en' | 'zh';
}

const TwitterForm: React.FC<TwitterFormProps> = ({ data, onChange, language }) => {
  const [error, setError] = useState<string>('');

  // 校验用户名或链接
  function validateProfile(input: string): boolean {
    if (!input) return false;
    if (/^@\w{1,15}$/.test(input)) return true;
    if (/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/\w{1,15}$/.test(input)) return true;
    return false;
  }
  // 校验推文内容
  function validateTweet(text: string): boolean {
    return !!text && text.length <= 280;
  }

  // 主页输入变化
  const handleProfileChange = (val: string) => {
    onChange({ ...data, username: val });
    if (!val) {
      setError('');
      return;
    }
    if (!validateProfile(val)) {
      setError(t('twitterProfileError', language));
    } else {
      setError('');
    }
  };
  // 推文输入变化
  const handleTweetChange = (val: string) => {
    onChange({ ...data, tweetText: val });
    if (!val) {
      setError('');
      return;
    }
    if (!validateTweet(val)) {
      setError(t('twitterShareError', language));
    } else {
      setError('');
    }
  };

  // 切换类型时清空输入和错误
  const handleTypeChange = (type: TwitterQRCodeType) => {
    onChange({ ...data, twitterType: type, username: '', tweetText: '' });
    setError('');
  };

  // 首次渲染时默认选中 profile
  useEffect(() => {
    if (!data.twitterType) {
      onChange({ ...data, twitterType: 'profile', username: '', tweetText: '' });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <button
          onClick={() => handleTypeChange('profile')}
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            data.twitterType === 'profile'
              ? 'bg-blue-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {t('twitterProfile', language)}
        </button>
        <button
          onClick={() => handleTypeChange('tweet')}
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            data.twitterType === 'tweet'
              ? 'bg-blue-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {t('twitterShare', language)}
        </button>
      </div>

      {data.twitterType === 'profile' ? (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('twitterProfile', language)}
          </label>
          <div className="relative">
            <Twitter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={data.username ?? ''}
              onChange={e => handleProfileChange(e.target.value)}
              placeholder={t('twitterProfilePlaceholder', language)}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {data.username && error && (
            <p className="mt-2 text-sm text-red-400">{error}</p>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('twitterShare', language)}
          </label>
          <textarea
            value={data.tweetText ?? ''}
            onChange={e => handleTweetChange(e.target.value)}
            placeholder={t('twitterSharePlaceholder', language)}
            rows={4}
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          {data.tweetText && error && (
            <p className="mt-2 text-sm text-red-400">{error}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TwitterForm; 